import type { ReactElement } from "react";
import { Users } from "lucide-react";
import { Badge, EmptyState, Stack, Text } from "@ux/primitives";
import { DataTable, type DataColumn } from "@ux/compositions";
import { formatTimestamp } from "../common/formatting";
import { formatUserDisplay } from "../users/display";
import type { UserDirectoryEntry } from "../users/types";
import type { WorkspaceView } from "../workspaces/types";
import type { WorkspaceAssignmentView } from "./types";

export interface WorkspaceAssignmentsListProps {
    readonly rows: readonly WorkspaceAssignmentView[];
    /**
     * Per-id resolution map (`useWorkspaceDisplayNames`). `null` values
     * mean "still loading" or "not found"; the row renderer treats both
     * the same and falls back to displaying the raw workspace id.
     */
    readonly workspaceIndex: ReadonlyMap<string, WorkspaceView | null>;
    readonly userIndex: ReadonlyMap<string, UserDirectoryEntry | null>;
    readonly selectedId: string | null;
    readonly onSelect: (row: WorkspaceAssignmentView) => void;
    readonly isLoading: boolean;
    readonly error: string | null;
}

export function WorkspaceAssignmentsList({
    rows,
    workspaceIndex,
    userIndex,
    selectedId,
    onSelect,
    isLoading,
    error,
}: WorkspaceAssignmentsListProps): ReactElement {
    const columns: readonly DataColumn<WorkspaceAssignmentView>[] = [
        {
            id: "occupant",
            header: "User",
            render: (row) => (
                <Stack gap={1}>
                    <Text role="body" weight="semibold">
                        {formatUserDisplay(userIndex.get(row.userId) ?? null, row.userId)}
                    </Text>
                    <Text role="muted">{describeWorkspace(row, workspaceIndex)}</Text>
                </Stack>
            ),
        },
        {
            id: "when",
            header: "Assigned",
            width: "180px",
            render: (row) => <Text role="muted">{formatTimestamp(row.assigned.timeStamp)}</Text>,
        },
        {
            id: "status",
            header: "Status",
            width: "110px",
            render: (row) =>
                row.isOpen ? (
                    <Badge tone="accent">Open</Badge>
                ) : (
                    <Badge tone="neutral">Released</Badge>
                ),
        },
    ];

    if (isLoading && rows.length === 0) {
        return <EmptyState icon={<Users size={28} />} title="Loading occupants…" />;
    }
    if (error !== null) {
        return (
            <EmptyState
                icon={<Users size={28} />}
                title="Could not load occupants"
                description={error}
            />
        );
    }

    return (
        <DataTable<WorkspaceAssignmentView>
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            selectedKey={selectedId}
            onSelect={onSelect}
            emptyContent={
                <EmptyState
                    icon={<Users size={28} />}
                    title="No occupants yet"
                    description="Assign a user to a workspace from the toolbar."
                />
            }
        />
    );
}

function describeWorkspace(
    row: WorkspaceAssignmentView,
    workspaceIndex: ReadonlyMap<string, WorkspaceView | null>,
): string {
    const ws = workspaceIndex.get(row.workspaceId);
    // Treat undefined (id not in the map) and null (resolved to 404 /
    // still loading) the same — show the raw id as a graceful fallback.
    if (ws !== undefined && ws !== null) {
        const friendly =
            ws.friendlyName !== null && ws.friendlyName !== undefined && ws.friendlyName !== ""
                ? ` (${ws.friendlyName})`
                : "";
        return `${ws.type} · ${ws.name}${friendly}`;
    }
    return `Workspace · ${row.workspaceId}`;
}
